import { useState, type FormEvent } from 'react'
import { Link } from 'react-router-dom'
import { ArrowRight } from 'lucide-react'
import { useI18n } from '@/i18n'
import { siteConfig } from '@/config/site.config'
import { isValidEmail } from '@/lib/format'
import { getIcon } from '@/lib/icons'
import { Container } from './Container'

/**
 * The footer: an ink block that closes the page. Deliberately the only large
 * dark area in light mode — it gives the layout a floor to stand on.
 */
export function SiteFooter() {
  const { t, tx } = useI18n()
  const [email, setEmail] = useState('')
  const [done, setDone] = useState(false)

  const subscribe = (e: FormEvent) => {
    e.preventDefault()
    if (!isValidEmail(email)) return
    setDone(true)
    setEmail('')
  }

  return (
    <footer
      data-upbo-id="site-footer"
      data-upbo-type="footer"
      data-upbo-label="Site footer"
      className="bg-ink text-on-ink"
    >
      <Container size="xl">
        <div className="py-16 sm:py-20 grid gap-12 lg:gap-16 md:grid-cols-2 lg:grid-cols-[1.4fr_1fr_1fr_1.3fr]">
          {/* ── Identity ─────────────────────────────────────────────────── */}
          <div className="flex flex-col gap-5">
            <div className="flex flex-col leading-none">
              <span className="font-display text-[30px] tracking-[0.22em] pl-[0.22em]">{siteConfig.name}</span>
              <span className="text-[9px] tracking-[0.24em] uppercase mt-2 text-on-ink/50">
                {tx(siteConfig.tagline)}
              </span>
            </div>
            <p className="text-sm text-on-ink/60 max-w-xs leading-relaxed">{t('footer.tagline')}</p>
            <div className="flex items-center gap-1 mt-1">
              {siteConfig.socials.map((s) => {
                const Icon = getIcon(s.icon)
                return (
                  <a
                    key={s.label}
                    href={s.href}
                    target="_blank"
                    rel="noreferrer noopener"
                    aria-label={s.label}
                    className="h-9 w-9 grid place-items-center rounded-squircle text-on-ink/55 hover:text-on-ink hover:bg-white/10 transition-colors"
                  >
                    <Icon size={16} strokeWidth={1.5} />
                  </a>
                )
              })}
            </div>
          </div>

          {/* ── Explore ──────────────────────────────────────────────────── */}
          <nav className="flex flex-col gap-4" aria-label={t('footer.explore')}>
            <span className="eyebrow text-on-ink/45">{t('footer.explore')}</span>
            {siteConfig.nav.map((item) => (
              <Link
                key={item.to}
                to={item.to}
                className="text-sm text-on-ink/70 hover:text-on-ink transition-colors w-fit link-underline"
              >
                {t(`nav.${item.key}`)}
              </Link>
            ))}
          </nav>

          {/* ── Salons ───────────────────────────────────────────────────── */}
          <div className="flex flex-col gap-4">
            <span className="eyebrow text-on-ink/45">{t('contact.salons')}</span>
            {siteConfig.salons.map((s) => (
              <div key={tx(s.city)} className="text-sm text-on-ink/70 leading-relaxed">
                <div className="text-on-ink">{tx(s.city)}</div>
                <div>{tx(s.address)}</div>
                <a href={`tel:${s.phone.replace(/\s/g, '')}`} className="hover:text-on-ink transition-colors">
                  {s.phone}
                </a>
              </div>
            ))}
          </div>

          {/* ── Newsletter ───────────────────────────────────────────────── */}
          <div className="flex flex-col gap-4">
            <span className="eyebrow text-on-ink/45">{t('footer.newsletterTitle')}</span>
            <p className="text-sm text-on-ink/60 leading-relaxed">{t('footer.newsletterBody')}</p>
            {done ? (
              <p className="text-sm text-primary">{t('footer.newsletterDone')}</p>
            ) : (
              <form onSubmit={subscribe} className="flex items-center border-b border-white/25 focus-within:border-white/60 transition-colors">
                <input
                  type="email"
                  required
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder={t('footer.newsletterPlaceholder')}
                  aria-label={t('footer.newsletterPlaceholder')}
                  data-upbo-type="input"
                  className="flex-1 h-11 bg-transparent text-sm text-on-ink placeholder:text-on-ink/40 focus:outline-none"
                />
                <button
                  type="submit"
                  aria-label={t('footer.newsletterCta')}
                  className="h-11 w-11 grid place-items-center text-on-ink/60 hover:text-on-ink transition-colors"
                >
                  <ArrowRight size={17} strokeWidth={1.5} />
                </button>
              </form>
            )}
          </div>
        </div>

        {/* ── Bottom bar ─────────────────────────────────────────────────── */}
        <div className="py-6 border-t border-white/10 flex flex-col sm:flex-row items-center justify-between gap-3 text-[11px] tracking-wide text-on-ink/45">
          <span>
            © {new Date().getFullYear()} {tx(siteConfig.legalName)}. {t('footer.rights')}
          </span>
          <div className="flex items-center gap-5">
            <span>{t('footer.legal')}</span>
          </div>
        </div>
      </Container>
    </footer>
  )
}
